import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { cancelAppointment, getAppointment, updateAppointment } from '../../api/appointments'
import { Button, ButtonLink } from '../../components/ui/Button'
import { Card, ErrorBox, SectionTitle } from '../../components/ui/Card'
import { useConfirm } from '../../components/ui/ConfirmModal'
import { useToast } from '../../components/ui/Toast'
import { useAuth } from '../../context/AuthContext'
import { useFetch } from '../../hooks/useFetch'
import { crumbLinkClass, infoLabelClass, infoValueClass } from '../../lib/tableStyles'
import type { AppointmentStatus, AppointmentType } from '../../types/appointments'
import { STATUS_COLORS, STATUS_LABELS, TYPE_COLORS, TYPE_LABELS } from '../../types/appointments'

function StatusBadge({ status }: { status: AppointmentStatus }) {
  const { color, bg } = STATUS_COLORS[status]
  return (
    <span
      className="inline-flex items-center px-3 py-[0.2rem] rounded-full text-[0.82rem] font-semibold"
      style={{ color, background: bg }}
    >
      {STATUS_LABELS[status]}
    </span>
  )
}

function TypeBadge({ type }: { type: AppointmentType }) {
  const { color, bg } = TYPE_COLORS[type]
  return (
    <span
      className="inline-flex items-center px-3 py-[0.2rem] rounded-full text-[0.82rem] font-semibold"
      style={{ color, background: bg }}
    >
      {TYPE_LABELS[type]}
    </span>
  )
}

function InfoField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-[1.1rem]">
      <div className={infoLabelClass}>{label}</div>
      <div className={infoValueClass}>{children}</div>
    </div>
  )
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric',
  })
}

export default function AppointmentDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const toast = useToast()
  const confirm = useConfirm()
  const [completing, setCompleting] = useState(false)

  const { data: appt, loading, error, refetch } = useFetch(
    () => getAppointment(Number(id)),
    [id],
  )

  // Pre-consult weight local state (synced from loaded appointment)
  const [weightStr, setWeightStr] = useState('')
  const [weightSaving, setWeightSaving] = useState(false)
  useEffect(() => {
    setWeightStr(appt?.pre_consult_weight_kg ?? '')
  }, [appt?.pre_consult_weight_kg])

  const handleWeightBlur = async () => {
    if (!appt) return
    const trimmed = weightStr.trim()
    const current = appt.pre_consult_weight_kg ?? ''
    if (trimmed === current) return
    if (trimmed !== '' && (isNaN(Number(trimmed)) || Number(trimmed) <= 0)) {
      setWeightStr(current)
      return
    }
    setWeightSaving(true)
    try {
      await updateAppointment(appt.id, { pre_consult_weight_kg: trimmed || null })
      toast.success('Weight saved.')
      refetch()
    } catch {
      toast.error('Failed to save weight.')
      setWeightStr(current)
    } finally {
      setWeightSaving(false)
    }
  }

  const handleMarkComplete = async () => {
    if (!appt) return
    setCompleting(true)
    try {
      await updateAppointment(appt.id, { status: 'completed' })
      toast.success('Appointment marked as complete.')
      refetch()
    } catch {
      toast.error('Failed to update appointment.')
    } finally {
      setCompleting(false)
    }
  }

  const handleCancel = async () => {
    if (!appt) return
    const ok = await confirm({
      title: 'Cancel appointment?',
      message: `This will mark the appointment on ${formatDateTime(appt.scheduled_at)} as cancelled. This action can be undone by editing the appointment.`,
      confirmLabel: 'Cancel Appointment',
    })
    if (!ok) return
    try {
      await cancelAppointment(appt.id)
      toast.success('Appointment cancelled.')
      refetch()
    } catch {
      toast.error('Failed to cancel appointment. Please try again.')
    }
  }

  if (loading) return <div className="text-text-2 py-8">Loading…</div>

  if (error || !appt) {
    return (
      <div>
        <ErrorBox>{error ?? 'Appointment not found.'}</ErrorBox>
        <Link to="/appointments" className={crumbLinkClass}>← Back to appointments</Link>
      </div>
    )
  }

  const isFinalised = appt.status === 'cancelled' || appt.status === 'completed'
  const canStartConsult = (appt.status === 'scheduled' || appt.status === 'confirmed')
    && !appt.consultation_id && user?.role !== 'org_admin'

  return (
    <div>
      <nav className="text-[0.85rem] text-text-3 mb-5 flex items-center gap-[0.4rem]">
        <Link to="/appointments" className={crumbLinkClass}>Appointments</Link>
        <span>›</span>
        <span>#{appt.id}</span>
      </nav>

      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="m-0 text-[1.5rem] font-bold text-text-heading">
            Appointment #{appt.id}
          </h1>
          <TypeBadge type={appt.appointment_type} />
          <StatusBadge status={appt.status} />
        </div>
        <div className="flex gap-2 flex-wrap">
          {appt.consultation_id ? (
            <ButtonLink to={`/consultations/${appt.consultation_id}`}>
              View Consultation →
            </ButtonLink>
          ) : canStartConsult && (
            <ButtonLink to={`/patients/${appt.patient_id}/consultations/new?appointment_id=${appt.id}`}>
              Start Consultation
            </ButtonLink>
          )}
          {!isFinalised && appt.status !== 'completed' && (
            <Button disabled={completing} onClick={() => void handleMarkComplete()}>
              {completing ? 'Saving…' : 'Mark Complete'}
            </Button>
          )}
          <ButtonLink to={`/appointments/${appt.id}/edit`} variant="outline">Edit</ButtonLink>
          <ButtonLink
            to={`/invoices/new?appointment_id=${appt.id}&client_id=${appt.client_id}&patient_id=${appt.patient_id}`}
            variant="outline"
          >
            Create Invoice
          </ButtonLink>
          {!isFinalised && (
            <Button variant="danger" onClick={() => void handleCancel()}>
              Cancel
            </Button>
          )}
          <Button variant="outline" onClick={() => void navigate(-1)}>← Back</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4 max-sm:grid-cols-1">
        <Card>
          <SectionTitle>Patient &amp; Client</SectionTitle>
          <InfoField label="Patient">
            {appt.patient_name ? (
              <Link to={`/patients/${appt.patient_id}`} className={crumbLinkClass}>
                {appt.patient_name}
              </Link>
            ) : `ID ${appt.patient_id}`}
          </InfoField>
          <InfoField label="Client">
            {appt.client_name ? (
              <Link to={`/clients/${appt.client_id}`} className={crumbLinkClass}>
                {appt.client_name}
              </Link>
            ) : `ID ${appt.client_id}`}
          </InfoField>
        </Card>

        <Card>
          <SectionTitle>Schedule</SectionTitle>
          <InfoField label="Date &amp; Time">
            {formatDateTime(appt.scheduled_at)}
          </InfoField>
          <InfoField label="Duration">
            {appt.duration_minutes} minutes
          </InfoField>
          <InfoField label="Veterinarian">
            {appt.veterinarian_name ?? <span className="text-text-4">Unassigned</span>}
          </InfoField>
        </Card>
      </div>

      <Card className="mb-4">
        <SectionTitle>Details</SectionTitle>
        <InfoField label="Reason for visit">
          {appt.reason ?? <span className="text-text-4">—</span>}
        </InfoField>
        <div className="mb-[1.1rem]">
          <div className={infoLabelClass}>Weight on arrival (kg)</div>
          {isFinalised ? (
            <div className={infoValueClass}>
              {appt.pre_consult_weight_kg ? `${appt.pre_consult_weight_kg} kg` : <span className="text-text-4">—</span>}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={weightStr}
                onChange={e => setWeightStr(e.target.value)}
                onBlur={() => void handleWeightBlur()}
                placeholder="e.g. 4.20"
                min={0}
                step={0.01}
                disabled={weightSaving}
                className="w-32 px-3 py-[0.45rem] rounded-[6px] border border-border-input bg-surface text-text text-sm focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent disabled:opacity-60"
              />
              <span className="text-text-3 text-sm">kg</span>
              {weightSaving && <span className="text-text-3 text-xs">Saving…</span>}
            </div>
          )}
        </div>
        <InfoField label="Notes">
          {appt.notes ? (
            <span className="whitespace-pre-wrap">{appt.notes}</span>
          ) : (
            <span className="text-text-4">—</span>
          )}
        </InfoField>
      </Card>

      <Card>
        <SectionTitle>Record</SectionTitle>
        <InfoField label="Created">{formatDate(appt.created_at)}</InfoField>
        <InfoField label="Last updated">{formatDate(appt.updated_at)}</InfoField>
      </Card>
    </div>
  )
}
